import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "bg-white border-[#E5E5E3] text-[#1A1A1A] focus-visible:ring-[#B8965A]";

/** Admin universal edit dialog for any user. */
export default function AdminUserEditDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", email: "", role: "buyer", status: "active",
    fiscal_code: "", address: "", phone: "",
    agent_approved: false, ndnca_signed: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({
      name: target.name || "",
      email: target.email || "",
      role: target.role || "buyer",
      status: target.status || "active",
      fiscal_code: target.fiscal_code || "",
      address: target.address || "",
      phone: target.phone || "",
      agent_approved: !!target.agent_approved,
      ndnca_signed: !!target.ndnca_signed,
    });
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${target.user_id}`, form);
      toast.success(`Utente aggiornato: ${form.email}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`admin-user-edit-${target.user_id}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Modifica membro
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#4A4A4A]">Nome</Label>
              <Input data-testid="admin-user-edit-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Email</Label>
              <Input data-testid="admin-user-edit-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#4A4A4A]">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-user-edit-role" className="bg-white border-[#E5E5E3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="admin-user-edit-status" className="bg-white border-[#E5E5E3]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Attivo</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="inactive">Inattivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[#4A4A4A]">Codice fiscale / P.IVA</Label>
            <Input data-testid="admin-user-edit-fc" className={inputCls} value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">Indirizzo</Label>
            <Input data-testid="admin-user-edit-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">Telefono</Label>
            <Input data-testid="admin-user-edit-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E5E5E3]">
            <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
              <input type="checkbox" checked={form.agent_approved}
                     data-testid="admin-user-edit-approved"
                     onChange={(e) => setForm({ ...form, agent_approved: e.target.checked })} />
              Approvato (agent_approved)
            </label>
            <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
              <input type="checkbox" checked={form.ndnca_signed}
                     data-testid="admin-user-edit-ndnca"
                     onChange={(e) => setForm({ ...form, ndnca_signed: e.target.checked })} />
              NDNCA firmato
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="admin-user-edit-save"
                  className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
            {busy ? "…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
